import { Flex, VStack, useToast, useDisclosure, Box } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import React, { useMemo, useState } from "react";
import { UploadAreaContainer } from "./style";
import ContractFiles from "@root/components/material-composition/ContractFiles";
import DrawingFiles from "@root/components/material-composition/DrawingFiles";
import { useDispatch } from "react-redux";
import { processMaterialComparison } from "@root/store/thunks/documentsActions";
import { FILE_TYPE, ROUTE_NAMES } from "@root/constants";
import { useNavigate } from "react-router-dom";
import { ViewHistoryDrawer } from "@root/components/dashboard/UploadDashboad.tsx/ViewHistoryDrawer";
import RefreshIcon from "@root/assets/icons/history-refresh-icon";
import { CommonButton } from "@root/components/common/button/commonButton";
import { ArrowUpRightIcon } from "@root/assets/icons/arrowup";
import ModelDisplay from "../dashboard/components/model-selection-toggler.tsx/model-text";
import { getChatType } from "@root/utils";
import { TourGuide } from "@root/components/tour-guide/TourGuide";
import { RestartTourButton } from "@root/components/tour-guide/RestartTourButton";
import { TourGuideHoverTooltip } from "@root/components/tour-guide/TourGuideHoverTooltip";
import { useMaterialComparisonTourGuide } from "@root/hooks/useMaterialComparisonTourGuide";

export const MaterialComparison: React.FC = () => {
  const [selectedContract, setSelectedContract] = useState<any>(null);
  const [selectedDrawing, setSelectedDrawing] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();
  const {
    isOpen: isHistoryOpen,
    onOpen: onHistoryOpen,
    onClose: onHistoryClose,
  } = useDisclosure();

  // Tour guide setup
  const {
    filesListContainerRef,
    startAnalysisRef,
    materialComparisonTourGuide,
    isMaterialComparison,
    contractUploadHover,
    drawingUploadHover,
    startAnalysisHover,
  } = useMaterialComparisonTourGuide();

  const handleContractSelection = (contract: any) => {
    setSelectedContract(contract);
  };

  const handleDrawingSelection = (drawing: any) => {
    setSelectedDrawing(drawing);
  };

  const handleProceedWithAnalysis = () => {
    setLoading(true);
    dispatch(
      processMaterialComparison({
        payload: {
          id: selectedDrawing?.id,
          drawing_fileName: selectedDrawing?.fileName,
          contract_fileName: selectedContract?.fileName,
        },
        onSuccess: () => {
          navigate(`${ROUTE_NAMES.LEGAL_REVIEW}/${selectedDrawing?.id}`);
          setLoading(false);
        },
        onError: () => {
          toast({
            title: t("upload_document.error_processing"),
            status: "error",
          });
          setLoading(false);
        },
      })
    );
  };

  const isAnalysisEnabled = selectedContract && selectedDrawing;
  const chatType = useMemo(() => {
    return getChatType(window.location.pathname);
  }, []);

  return (
    <VStack
      px={"30px"}
      py={"12px"}
      w={"100%"}
      spacing={6}
      align="stretch"
      position="relative"
      className="material-comparison-container"
      gap="20px"
    >
      <Flex
        justifyContent={"space-between"}
        alignItems={"center"}
        borderBottom="1px solid #E0E0E0"
        pb="10px"
      >
        {/* <Text fontSize={"26px"} fontWeight="700">
              {t("materialCamparison.materialSpecification")}
            </Text> */}
        <ModelDisplay modelKey={chatType} />
        <Flex gap={"20px"}>
          <Box ref={startAnalysisRef} {...startAnalysisHover.hoverProps}>
            <CommonButton
              variant="primary"
              onClick={handleProceedWithAnalysis}
              isDisabled={!isAnalysisEnabled}
              endIcon={<ArrowUpRightIcon size={25} color="white" />}
              isLoading={loading}
            >
              {t("upload_document.start_processing")}
            </CommonButton>
          </Box>
          <CommonButton
            variant="secondary"
            startIcon={<RefreshIcon />}
            onClick={onHistoryOpen}
          >
            {t("dashboard.viewReport")}
          </CommonButton>
        </Flex>
      </Flex>
      {/* CTA now uses HeadingText above; keep commented demo button translated if needed */}
      <UploadAreaContainer ref={filesListContainerRef}>
        {/* Upload Contract */}
        <ContractFiles
          onFileSelect={handleContractSelection}
          selectedFile={selectedContract}
        />

        {/* Upload Drawing */}
        <DrawingFiles
          onFileSelect={handleDrawingSelection}
          selectedFile={selectedDrawing}
        />
      </UploadAreaContainer>

      {/* Restart Tour Button */}
      {isMaterialComparison && !materialComparisonTourGuide.isActive && (
        <Flex justifyContent="flex-end">
          <RestartTourButton
            onClick={materialComparisonTourGuide.startTour}
            position="static"
          />
        </Flex>
      )}

      {/* Hover tooltips for contract upload */}
      {contractUploadHover.isHovered && contractUploadHover.tooltipData && (
        <TourGuideHoverTooltip
          title={contractUploadHover.tooltipData.title}
          description={contractUploadHover.tooltipData.description}
          targetSelector='[data-tour-item="material-contract-upload"]'
          position={contractUploadHover.tooltipData.position}
          offset={contractUploadHover.tooltipData.offset}
          enabled={!materialComparisonTourGuide.isActive}
          isVisible={contractUploadHover.isHovered}
        />
      )}

      {/* Hover tooltips for drawing upload */}
      {drawingUploadHover.isHovered && drawingUploadHover.tooltipData && (
        <TourGuideHoverTooltip
          title={drawingUploadHover.tooltipData.title}
          description={drawingUploadHover.tooltipData.description}
          targetSelector='[data-tour-item="material-drawing-upload"]'
          position={drawingUploadHover.tooltipData.position}
          offset={drawingUploadHover.tooltipData.offset}
          enabled={!materialComparisonTourGuide.isActive}
          isVisible={drawingUploadHover.isHovered}
        />
      )}

      {/* Hover tooltip for start analysis */}
      {startAnalysisHover.isHovered && startAnalysisHover.tooltipData && (
        <TourGuideHoverTooltip
          title={startAnalysisHover.tooltipData.title}
          description={startAnalysisHover.tooltipData.description}
          targetRef={startAnalysisRef}
          position={startAnalysisHover.tooltipData.position}
          offset={startAnalysisHover.tooltipData.offset}
          enabled={!materialComparisonTourGuide.isActive}
          isVisible={startAnalysisHover.isHovered}
        />
      )}

      <ViewHistoryDrawer
        isOpen={isHistoryOpen}
        onClose={onHistoryClose}
        type={FILE_TYPE.FINISHING_MATERIAL_REQUEST}
      />

      {/* Tour Guide Component */}
      {isMaterialComparison &&
        materialComparisonTourGuide.isActive &&
        materialComparisonTourGuide.currentStepData && (
          <TourGuide
            isActive={materialComparisonTourGuide.isActive}
            currentStep={materialComparisonTourGuide.currentStepData}
            currentStepIndex={materialComparisonTourGuide.currentStep}
            totalSteps={materialComparisonTourGuide.totalSteps}
            onNext={materialComparisonTourGuide.nextStep}
            onBack={materialComparisonTourGuide.previousStep}
            onClose={materialComparisonTourGuide.stopTour}
            targetRef={
              materialComparisonTourGuide.currentStepData.id === "files-list"
                ? filesListContainerRef
                : materialComparisonTourGuide.currentStepData.id ===
                  "start-analysis"
                ? startAnalysisRef
                : undefined
            }
            targetSelector={
              materialComparisonTourGuide.currentStepData.id ===
                "sidebar-material" ||
              materialComparisonTourGuide.currentStepData.id ===
                "contract-upload" ||
              materialComparisonTourGuide.currentStepData.id ===
                "drawing-upload"
                ? materialComparisonTourGuide.currentStepData.targetSelector
                : undefined
            }
          />
        )}
    </VStack>
  );
};
